import {MOON_500} from '@wandb/weave/common/css/color.styles';
import {Alert} from '@wandb/weave/components/Alert';
import {Button} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {ReusableDrawer} from '../../ReusableDrawer';
import {LLMDropdownLoaded} from '../PlaygroundPage/PlaygroundChat/LLMDropdown';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';
import {refStringToName} from './common';
import {LabeledTextArea, LabeledTextField, LoadingSelect} from './components';
import {BORDER_COLOR} from './constants';
import {useEvaluationPlaygroundPageContext} from './context';
import {useDatasetColumnsContext} from './DatasetColumnsContext';
import {clientBound, hookify} from './hooks';
import {Column, ConfigSection, Row} from './layout';
import {
  getLatestModelRefs,
  getSimplifiedLLMStructuredCompletionModel,
  publishSimplifiedLLMStructuredCompletionModel,
} from './query';
import {defaultModelConfigPayload} from './state';
import {SimplifiedLLMStructuredCompletionModel} from './types';
import {VersionedObjectPicker} from './VersionedObjectPicker';

// Default option for creating a new model from scratch
const newModelOption = {
  label: 'New Model',
  value: 'new-model',
};

// Create the hook for fetching model refs
// This wraps the async query function in a React hook that manages loading/error states
const useLatestModelRefs = clientBound(hookify(getLatestModelRefs));
const useSimplifiedModel = clientBound(
  hookify(getSimplifiedLLMStructuredCompletionModel)
);

interface SimplifiedModelConfigProps {
  entity: string;
  project: string;
  modelRef: string | null;
  modelNdx: number;
  onModelSaved: (modelNdx: number, newRef: string) => void;
  onOpenAdvancedSettings: (
    modelNdx: number,
    simplifiedConfig: SimplifiedLLMStructuredCompletionModel
  ) => void;
  onSaveStateChange: (
    canSave: boolean,
    saveHandler: () => Promise<string>
  ) => void;
}

/**
 * Simplified model configuration form for basic LLM models.
 * Provides an inline form for models that don't require advanced configuration.
 */
const SimplifiedModelConfig: React.FC<SimplifiedModelConfigProps> = ({
  entity,
  project,
  modelRef,
  modelNdx,
  onModelSaved,
  onOpenAdvancedSettings,
  onSaveStateChange,
}) => {
  const client = useGetTraceServerClientContext()();
  useDatasetColumnsContext();
  // Local state for the simplified form
  const [config, setConfig] = useState<SimplifiedLLMStructuredCompletionModel>(
    defaultModelConfigPayload
  );

  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(!modelRef); // New models start with unsaved changes
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const simplifiedModelQuery = useSimplifiedModel(
    entity,
    project,
    modelRef ?? ''
  );

  // Reset unsaved changes immediately when ref changes to an existing model
  useEffect(() => {
    if (modelRef) {
      // Immediately reset unsaved changes when switching to an existing model
      setHasUnsavedChanges(false);
    } else {
      // For new models, mark as having unsaved changes
      setHasUnsavedChanges(true);
    }
  }, [modelRef]);

  // Load existing model configuration when ref changes
  useEffect(() => {
    if (
      modelRef &&
      simplifiedModelQuery.data !== null &&
      !simplifiedModelQuery.loading
    ) {
      setConfig(simplifiedModelQuery.data);
      setInitialLoadComplete(true);
    } else if (!modelRef) {
      // For new models, mark as loaded immediately
      setInitialLoadComplete(true);
    }
  }, [modelRef, simplifiedModelQuery.data, simplifiedModelQuery.loading]);

  // Track unsaved changes
  const updateConfig = useCallback(
    (updates: Partial<SimplifiedLLMStructuredCompletionModel>) => {
      setConfig(prev => ({...prev, ...updates}));
      setHasUnsavedChanges(true);
    },
    []
  );

  // Reset to original config (either loaded data or default)
  const handleReset = useCallback(() => {
    if (modelRef && simplifiedModelQuery.data) {
      // Reset to loaded data for existing models
      setConfig(simplifiedModelQuery.data);
    } else {
      // Reset to default for new models
      setConfig(defaultModelConfigPayload);
    }
    setHasUnsavedChanges(false);
  }, [modelRef, simplifiedModelQuery.data]);

  // Validation: all fields are required
  const isValid =
    config.name.trim() !== '' &&
    config.systemPrompt.trim() !== '' &&
    config.llmModelId.trim() !== '';

  const handleSave = useCallback(async () => {
    if (!isValid || isSaving) return '';

    setIsSaving(true);
    try {
      // Save the model directly
      const modelRef = await publishSimplifiedLLMStructuredCompletionModel(
        client,
        entity,
        project,
        config
      );

      if (modelRef) {
        setHasUnsavedChanges(false);
        onModelSaved(modelNdx, modelRef);
        return modelRef; // Return the ref for the save-all handler
      }
      return '';
    } catch (error) {
      console.error('Failed to save model:', error);
      // TODO: Add proper error handling
      return '';
    } finally {
      setIsSaving(false);
    }
  }, [
    isValid,
    isSaving,
    client,
    entity,
    project,
    config,
    modelNdx,
    onModelSaved,
  ]);

  // Check if this model qualifies for simplified config
  const qualifies = !modelRef || simplifiedModelQuery.data !== null;
  const isLoading = modelRef && simplifiedModelQuery.loading;

  // Notify parent about save state changes
  useEffect(() => {
    const canSave = hasUnsavedChanges && isValid && !isSaving;
    onSaveStateChange(canSave, canSave ? handleSave : (null as any));
  }, [hasUnsavedChanges, isValid, isSaving, handleSave, onSaveStateChange]);

  // For new models, if LLM is auto-selected, mark as having unsaved changes
  useEffect(() => {
    if (!modelRef && config.llmModelId && initialLoadComplete) {
      setHasUnsavedChanges(true);
    }
  }, [modelRef, config.llmModelId, initialLoadComplete]);

  // Show loading state while checking if model qualifies
  if (isLoading || !initialLoadComplete) {
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: '#fff',
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: '14px',
          color: MOON_500,
          textAlign: 'center',
        }}>
        Loading model configuration...
      </div>
    );
  }

  // Show advanced configuration notice for complex models
  if (!qualifies) {
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: '#fff',
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: '14px',
          color: MOON_500,
          fontStyle: 'italic',
        }}>
        This model uses advanced configuration - please edit in playground.
        {/* TODO: Once our sidebar editor is flexible enough, open it here */}
        {/* <Button
          variant="ghost"
          size="small"
          onClick={() => onOpenAdvancedSettings(modelNdx, config)}
          style={{padding: '2px 8px', fontSize: '14px'}}>
          Edit settings →
        </Button> */}
      </div>
    );
  }

  return (
    <Column style={{}}>
      <Column
        style={{
          padding: '12px',
          backgroundColor: '#fff',
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          gap: '16px',
        }}>
        <LabeledTextField
          label="Name"
          placeholder="Model name"
          value={config.name}
          onChange={value => updateConfig({name: value})}
        />

        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: 600,
              fontSize: '16px',
            }}>
            <span>LLM Model</span>
          </div>
          <Tailwind>
            <LLMDropdownLoaded
              className="w-full"
              hideSavedModels
              value={config.llmModelId || ''}
              isTeamAdmin={false}
              direction={{horizontal: 'right'}}
              selectFirstAvailable={true}
              onChange={(modelValue: string) => {
                updateConfig({llmModelId: modelValue});
              }}
            />
          </Tailwind>
        </div>

        <LabeledTextArea
          label="System prompt"
          placeholder="Enter the system prompt (e.g., 'You are a helpful assistant that...')"
          value={config.systemPrompt}
          onChange={e => updateConfig({systemPrompt: e.target.value})}
          rows={3}
          instructions={
            <>
              This system prompt will be used to initialize the model.
              <br />
              The{' '}
              <code style={{fontSize: '12px', fontWeight: 600}}>
                user_input
              </code>{' '}
              from your dataset will be sent as a separate message.
            </>
          }
        />
      </Column>

      {/* Footer with reset and save buttons */}
      <Row
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px',
          backgroundColor: '#fff',
          border: `1px solid ${BORDER_COLOR}`,
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
        }}>
        <Button
          variant="ghost"
          onClick={handleReset}
          disabled={!hasUnsavedChanges}
          tooltip="Reset changes">
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!isValid || !hasUnsavedChanges || isSaving}
          tooltip="Save model configuration">
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Row>
    </Column>
  );
};

/**
 * Drawer component for configuring model details.
 * Provides a form interface for editing model properties like name, prompts, and output schema.
 *
 * @param entity - The Weave entity (organization/user)
 * @param project - The Weave project
 * @param open - Whether the drawer is open
 * @param onClose - Callback when drawer closes, receives new model ref if saved
 * @param initialModel - Existing model data to edit (optional)
 */
const ModelDrawer: React.FC<{
  entity: string;
  project: string;
  open: boolean;
  onClose: (newModelRef?: string) => void;
  initialModelRef?: string;
}> = ({entity, project, open, onClose, initialModelRef}) => {
  // const modelFormRef = useRef<ModelConfigurationFormRef | null>(null);

  const onSave = useCallback(async () => {}, []);

  return (
    <ReusableDrawer
      open={open}
      onClose={() => onClose()}
      title="Model Configuration"
      onSave={onSave}>
      <Tailwind>TODO</Tailwind>
    </ReusableDrawer>
  );
};

/**
 * Individual model row component with object picker and simplified configuration.
 * Handles both simple and advanced model configurations.
 */
const ModelRow: React.FC<{
  entity: string;
  project: string;
  model: {originalSourceRef: string | null};
  modelNdx: number;
  updateModelRef: (modelNdx: number, ref: string | null) => void;
  deleteModel: (modelNdx: number) => void;
  setCurrentlyEditingModelNdx: (
    ndx: number,
    simplifiedConfig?: SimplifiedLLMStructuredCompletionModel
  ) => void;
  modelRefsQuery: ReturnType<typeof useLatestModelRefs>;
  onRegisterSaveHandler: (
    modelNdx: number,
    handler: (() => Promise<string>) | null
  ) => void;
}> = ({
  entity,
  project,
  model,
  modelNdx,
  updateModelRef,
  deleteModel,
  setCurrentlyEditingModelNdx,
  modelRefsQuery,
  onRegisterSaveHandler,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleRefChange = useCallback(
    (ref: string | null) => {
      updateModelRef(modelNdx, ref);
    },
    [modelNdx, updateModelRef]
  );

  const handleDelete = useCallback(() => {
    deleteModel(modelNdx);
  }, [modelNdx, deleteModel]);

  const handleSaveStateChange = useCallback(
    (canSave: boolean, handler: () => Promise<string>) => {
      // Register/unregister with parent
      onRegisterSaveHandler(modelNdx, canSave ? handler : null);
    },
    [modelNdx, onRegisterSaveHandler]
  );

  // Show loading state for individual dropdowns if query is loading
  if (modelRefsQuery.loading) {
    return (
      <Row style={{alignItems: 'center', gap: '8px'}}>
        <Button icon="chevron-next" variant="ghost" disabled />
        <div style={{flex: 1}}>
          <LoadingSelect />
        </div>
        <Button icon="close" variant="ghost" disabled />
      </Row>
    );
  }

  return (
    <Column
      style={{
        borderRadius: '6px',
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      }}>
      <Row
        style={{
          position: 'sticky',
          top: -2,
          zIndex: 1,
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#fff',
          borderTop: `1px solid ${BORDER_COLOR}`,
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          borderTopLeftRadius: '6px',
          borderTopRightRadius: isExpanded ? '6px' : '6px',
          borderBottomLeftRadius: isExpanded ? '0px' : '6px',
          borderBottomRightRadius: isExpanded ? '0px' : '6px',
          borderBottom: `1px solid ${BORDER_COLOR}`,
          padding: '8px',
        }}>
        <Button
          icon={isExpanded ? 'chevron-down' : 'chevron-next'}
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          tooltip={
            isExpanded
              ? 'Collapse model configuration'
              : 'Expand model configuration'
          }
        />
        <div style={{flex: 1}}>
          <VersionedObjectPicker
            entity={entity}
            project={project}
            objectType="model"
            selectedRef={model.originalSourceRef}
            onRefChange={handleRefChange}
            latestObjectRefs={modelRefsQuery.data ?? []}
            loading={modelRefsQuery.loading}
            newOptions={[{label: 'New Model', value: 'new-model'}]}
            allowNewOption={true}
          />
        </div>
        <Button icon="close" variant="ghost" onClick={handleDelete} />
      </Row>
      {isExpanded && (
        <SimplifiedModelConfig
          entity={entity}
          project={project}
          modelRef={model.originalSourceRef}
          modelNdx={modelNdx}
          onModelSaved={updateModelRef}
          onOpenAdvancedSettings={setCurrentlyEditingModelNdx}
          onSaveStateChange={handleSaveStateChange}
        />
      )}
    </Column>
  );
};

/**
 * Configuration section for managing models in the evaluation.
 * Allows users to add, edit, and remove models that will be evaluated.
 */
export interface ModelsConfigSectionRef {
  saveAllUnsavedAndGetRefs: () => Promise<string[]>;
}

export const ModelsConfigSection = React.forwardRef<
  ModelsConfigSectionRef,
  {
    entity: string;
    project: string;
    error?: string;
    onValidationChanged?: (hasErrors: boolean) => void;
  }
>(({entity, project, error, onValidationChanged}, ref) => {
  const {config, editConfig} = useEvaluationPlaygroundPageContext();
  const {columns: datasetColumns} = useDatasetColumnsContext();
  const modelRefsQuery = useLatestModelRefs(entity, project);

  // Keep track of save handlers for each model
  const saveHandlersRef = useRef<Map<number, () => Promise<string>>>(new Map());

  // Keep track of newly saved refs to avoid stale closure issue
  const newlySavedRefsRef = useRef<Map<number, string>>(new Map());

  const models = config.models;

  // Simple model-level validation: LLMStructuredCompletionModel requires 'user_input' column
  const modelValidationError = useMemo(() => {
    const requiresUserInput = true; // current simplified models are structured completion
    if (requiresUserInput && !datasetColumns.includes('user_input')) {
      return true;
    }
    return false;
  }, [datasetColumns]);
  useEffect(() => {
    onValidationChanged?.(modelValidationError);
  }, [modelValidationError, onValidationChanged]);

  const addModel = useCallback(() => {
    editConfig(draft => {
      draft.models.push({
        originalSourceRef: null,
      });
    });
  }, [editConfig]);

  const modelOptions = useMemo(() => {
    return [
      {
        label: 'Create new model',
        options: [newModelOption],
      },
      {
        label: 'Load existing model',
        options:
          modelRefsQuery.data?.map(ref => ({
            label: refStringToName(ref),
            value: ref,
          })) ?? [],
      },
    ];
  }, [modelRefsQuery.data]);

  const deleteModel = useCallback(
    (modelNdx: number) => {
      editConfig(draft => {
        draft.models.splice(modelNdx, 1);
      });
      // Clean up save handler when deleting
      saveHandlersRef.current.delete(modelNdx);
    },
    [editConfig]
  );

  const registerSaveHandler = useCallback(
    (modelNdx: number, handler: (() => Promise<string>) | null) => {
      if (handler) {
        saveHandlersRef.current.set(modelNdx, handler);
      } else {
        saveHandlersRef.current.delete(modelNdx);
      }
    },
    []
  );

  const [currentlyEditingModelNdx, setCurrentlyEditingModelNdx] = useState<
    number | null
  >(null);

  const updateModelRef = useCallback(
    (modelNdx: number, ref: string | null) => {
      editConfig(draft => {
        if (ref === 'new-model' || ref === null) {
          // Reset to empty model
          draft.models[modelNdx].originalSourceRef = null;
        } else {
          // Set the selected model ref
          draft.models[modelNdx].originalSourceRef = ref;
        }
      });
    },
    [editConfig]
  );

  // Handle opening advanced settings
  const handleOpenAdvancedSettings = useCallback(
    (
      modelNdx: number | null,
      simplifiedConfig?: SimplifiedLLMStructuredCompletionModel
    ) => {
      setCurrentlyEditingModelNdx(modelNdx);
    },
    []
  );

  // Expose method via ref to get current model refs for evaluation
  useImperativeHandle(
    ref,
    () => ({
      saveAllUnsavedAndGetRefs: async () => {
        // Clear previous saves
        newlySavedRefsRef.current.clear();

        // Save all unsaved models and capture their refs
        const savePromises = Array.from(saveHandlersRef.current.entries()).map(
          async ([idx, handler]) => {
            const ref = await handler();
            if (ref) {
              newlySavedRefsRef.current.set(idx, ref);
            }
          }
        );
        await Promise.all(savePromises);

        // Build the list of all refs, using newly saved ones where available
        const allRefs: string[] = [];
        models.forEach((model, idx) => {
          const newRef = newlySavedRefsRef.current.get(idx);
          const ref = newRef || model.originalSourceRef;
          if (ref) {
            allRefs.push(ref);
          }
        });

        return allRefs;
      },
    }),
    [models]
  );

  return (
    <ConfigSection
      title="Models"
      icon="model"
      headerAction={
        <Button
          icon="add-new"
          variant="ghost"
          onClick={() => {
            addModel();
          }}>
          Add Model
        </Button>
      }>
      <Column style={{gap: '8px'}}>
        {modelValidationError && (
          <Alert severity="warning" style={{marginBottom: '8px'}}>
            Models require a dataset column named 'user_input'
          </Alert>
        )}
        {models.map((model, modelNdx) => {
          let selectedOption = newModelOption;
          const options = [...modelOptions];

          if (model.originalSourceRef) {
            const name = refStringToName(model.originalSourceRef);
            selectedOption = {
              label: name,
              value: model.originalSourceRef,
            };
            // Add current selection if it's not in the list (e.g., from another project)
            const allOptions = options.flatMap(group => group.options);
            if (
              !allOptions.find(opt => opt.value === model.originalSourceRef)
            ) {
              // Add to the existing models group
              options[1].options.unshift(selectedOption);
            }
          }

          return (
            <ModelRow
              key={modelNdx}
              entity={entity}
              project={project}
              model={model}
              modelNdx={modelNdx}
              updateModelRef={updateModelRef}
              deleteModel={deleteModel}
              setCurrentlyEditingModelNdx={handleOpenAdvancedSettings}
              modelRefsQuery={modelRefsQuery}
              onRegisterSaveHandler={registerSaveHandler}
            />
          );
        })}
        <ModelDrawer
          entity={entity}
          project={project}
          open={currentlyEditingModelNdx !== null}
          onClose={newModelRef => {
            if (newModelRef && currentlyEditingModelNdx !== null) {
              editConfig(draft => {
                // Update the model with the new reference
                draft.models[currentlyEditingModelNdx].originalSourceRef =
                  newModelRef;
              });
            }
            setCurrentlyEditingModelNdx(null);
          }}
          initialModelRef={
            currentlyEditingModelNdx !== null
              ? (models[currentlyEditingModelNdx].originalSourceRef ??
                undefined)
              : undefined
          }
        />
      </Column>
    </ConfigSection>
  );
});
